import { getAssetsPath } from "./pathConfig";
// import { FEATURES_CONFIG } from "../featuresConfig"; // only the Troika face used it
import { ASSETS } from "../assets";

// Use centralized path configuration
const BASE_PATH = getAssetsPath();


// Font family string — Noto Sans for all themes
const THEME_FONT_FAMILY = '"Noto Sans", -apple-system, blinkmacsystemfont, "Segoe UI", "Oxygen", "Cantarell", "Roboto", "Fira Sans", "Droid Sans", "Helvetica", "Helvetica Neue", "Arial", sans-serif';

// ── Noto Sans CYRILLIC (ported from Zeus, 2026-10-28) ──
//
// The Latin files hold no Cyrillic, and "NotoSans-Black" / plain "Noto Sans"
// carry no fallback chain, so Russian fell to the browser's default SERIF —
// Loc QA 2026-10-28: "Text font seems" (RU start page, popups). Same weights,
// google-webfonts-helper's v42 cyrillic + cyrillic-ext subset (900 added for
// the animator's Black). `unicode-range` keeps the browser from fetching them
// for any other language; preloadFonts loads them only for a Cyrillic locale,
// because the canvas cannot wait for a lazy font.
const CYRILLIC_UNICODE_RANGE =
  "U+0301, U+0400-045F, U+0460-052F, U+0490-0491, U+04B0-04B1, U+1C80-1C8A, U+20B4, U+2116, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F";
const CYRILLIC_LOCALES = ["ru"];
const NOTO_WEIGHT_FILES = [
  { weight: "400", suffix: "regular" },
  { weight: "500", suffix: "500" },
  { weight: "600", suffix: "600" },
  { weight: "700", suffix: "700" },
  { weight: "800", suffix: "800" },
  { weight: "900", suffix: "900" },
];
const cyrillicUrl = (suffix) =>
  `${BASE_PATH}/fonts/noto-sans/noto-sans-v42-cyrillic_cyrillic-ext-${suffix}.woff2`;

/**
 * Generate @font-face declarations for Noto Sans (all weights)
 */
const getThemeFontFaces = () => {
  return `
    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 400;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-regular.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 500;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-500.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 600;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-600.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 700;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-700.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 800;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-800.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 900;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-900.woff2") format("woff2");
    }
${NOTO_WEIGHT_FILES.map(
  ({ weight, suffix }) => `
    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: ${weight};
      src: url("${cyrillicUrl(suffix)}") format("woff2");
      unicode-range: ${CYRILLIC_UNICODE_RANGE};
    }`,
).join("")}

    @font-face {
      font-display: swap;
      font-family: "NotoSans-Black";
      font-style: normal;
      font-weight: normal;
      src: url("${cyrillicUrl("900")}") format("woff2");
      unicode-range: ${CYRILLIC_UNICODE_RANGE};
    }`;
};

/**
 * Generate @font-face declarations for registered web fonts (from assets.js)
 * Automatically picks up any fonts added to ASSETS.webFonts
 */
const getWebFontFaces = () => {
  if (!ASSETS.webFonts?.length) return '';
  return ASSETS.webFonts.map(font => `
    @font-face {
      font-display: swap;
      font-family: "${font.name}";
      font-style: normal;
      font-weight: normal;
      src: url("${font.url}") format("${font.format}");
    }`).join('');
};

/**
 * Injects CSS styles dynamically into the document
 * This allows bundle.js to be self-contained - fonts and styles work wherever it's injected
 */
export const injectStyles = () => {
  // Check if styles are already injected (prevents duplicate injection)
  if (document.getElementById("injected-styles")) {
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.id = "injected-styles";

  // Build CSS string with all styles
  const css = `
    /* ==========================================================================
       FONT DECLARATIONS
       ========================================================================== */

    ${/* Troika — removed from the game (Vladimir, 2026-09-30: "Troika can go"):
    ${FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ? `
    @font-face {
      font-display: swap;
      font-family: "Troika";
      font-style: normal;
      font-weight: normal;
      src: url("${BASE_PATH}/fonts/troika/troika.otf") format("opentype");
    }` : ''}
    */ ''}

    ${getThemeFontFaces()}

    ${getWebFontFaces()}

    /* ==========================================================================
       CSS RESET & BASE STYLES
       ========================================================================== */

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    html,
    body {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      overflow: hidden;
      background-color: #000000;
      --game-height: 100vh;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    html::-webkit-scrollbar,
    body::-webkit-scrollbar {
      display: none;
    }

    body {
      font-family: ${THEME_FONT_FAMILY};
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: none;
    }

    img {
      display: block;
      -webkit-user-drag: none;
      user-drag: none;
      -webkit-touch-callout: none;
    }

    /* Game container - clips the canvas to viewport */
    #game {
      width: 100%;
      height: 100%;
      overflow: hidden !important;
      position: relative;
      font-family: ${THEME_FONT_FAMILY};
    }

    /* Canvas fills viewport but PixiJS renders full stage content */
    /* Canvas should maintain aspect ratio and be centered */
    canvas {
      display: block;
      max-width: 100%;
      max-height: 100%;
      width: auto !important;
      height: auto !important;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `;

  styleElement.textContent = css;
  document.head.appendChild(styleElement);
};

/**
 * Preload custom fonts to ensure they're available for PIXI
 */
const preloadFonts = async () => {
  if (!document.fonts || !window.FontFace) {
    return; // Browser doesn't support Font Loading API
  }

  // Troika used to be hard-coded here behind HOLD_AND_WIN.ENABLED. It is now a
  // normal entry in ASSETS.webFonts, so the generic loop below both declares
  // its @font-face and preloads it — no feature flag involved.

  // Preload registered web fonts (from assets.js)
  if (ASSETS.webFonts?.length) {
    const webFontPromises = ASSETS.webFonts.map(async (font) => {
      try {
        const face = new FontFace(
          font.name,
          `url(${font.url}) format("${font.format}")`
        );
        await face.load();
        document.fonts.add(face);
      } catch (error) {
        // Web font preload failed — fallback fonts will be used
      }
    });
    await Promise.all(webFontPromises);
  }

  try {
    // ── Preload every weight the game actually uses — 400 to 800 ──
    //
    // Every entry here is a request AND a boot-blocking await, so an unused one
    // costs twice. 300 and 900 were shipped and preloaded and referenced by
    // nothing: not the live theme, not `primary/`, not Pot Collection, and not
    // by any computed weight. Removed 2026-09-17 with their files and their
    // @font-face blocks above.
    //
    // If a design ever asks for Light or Black, add the file, the face above
    // and the entry here together — a weight with no file silently falls back
    // to the nearest one that has been loaded.
    const notoWeights = [
      { weight: '400', file: 'noto-sans-v42-latin-regular.woff2' },
      { weight: '500', file: 'noto-sans-v42-latin-500.woff2' },
      { weight: '600', file: 'noto-sans-v42-latin-600.woff2' },
      { weight: '700', file: 'noto-sans-v42-latin-700.woff2' },
      { weight: '800', file: 'noto-sans-v42-latin-800.woff2' },
      // Black — the animator's text styles (NotoSans-Black, 2026-09-30),
      // file from Mad Labs.
      { weight: '900', file: 'noto-sans-v42-latin-900.woff2' },
    ];

    const notoFaces = notoWeights.map(({ weight, file }) =>
      new FontFace(
        'Noto Sans',
        `url(${BASE_PATH}/fonts/noto-sans/${file}) format("woff2")`,
        { weight }
      )
    );

    // The Cyrillic subset, for a Cyrillic locale only — see NOTO_WEIGHT_FILES.
    // The animator's "NotoSans-Black" (an ASSETS.webFonts name) gets the 900.
    const locale = String(window.__OPTIONS__?.locale || '').toLowerCase();
    if (CYRILLIC_LOCALES.some((code) => locale === code || locale.startsWith(`${code}-`))) {
      for (const { weight, suffix } of NOTO_WEIGHT_FILES) {
        notoFaces.push(
          new FontFace(
            'Noto Sans',
            `url(${cyrillicUrl(suffix)}) format("woff2")`,
            { weight, unicodeRange: CYRILLIC_UNICODE_RANGE }
          )
        );
      }
      notoFaces.push(
        new FontFace(
          'NotoSans-Black',
          `url(${cyrillicUrl('900')}) format("woff2")`,
          { unicodeRange: CYRILLIC_UNICODE_RANGE }
        )
      );
    }

    await Promise.all(notoFaces.map(face => face.load()));
    notoFaces.forEach(face => document.fonts.add(face));

  } catch (error) {
    // Font preload failed - fallback fonts will be used
  }
};

/**
 * Initializes all styles - call this once at app startup
 * This makes bundle.js completely self-contained
 */
export const initializeStyles = async () => {
  injectStyles();
  await preloadFonts();
};
